import { AuthConsumerRenderProps, withAuthContext } from 'auth/AuthContext';
import axios from 'axios';
import Input from 'components/final-form/Input';
import PraxStyledSegment from 'components/layout/PraxStyledSegment';
import PxGrid from 'components/layout/PxGrid';
import StyledViewTableRow from 'components/layout/StyledViewTableRow';
import VirtualizedTable from 'components/layout/VirtualizedTable';
import { NotificationSystemProps, withNotificationSystemContext } from 'context/NotificationSysemContext';
import React, { useEffect, useRef, useState } from 'react';
import { Field, Form as FinalForm, FormSpy } from 'react-final-form';
import { MessageSourceProps, withMessages } from 'react-message-source';
import { Button, Dimmer, Grid, Icon, Loader, Pagination, PaginationProps } from 'semantic-ui-react';
import {
  findFileArchiveEntries,
  getFileDownloadKey,
  getFileUrl,
  removeFileArchiveDuplicates
} from 'services/fileArchiveServices';
import styled from "styled-components";
import { debounce } from 'ts-debounce';
import { FileArchiveDto, PdfResponse } from 'ts-types/api.types';
import { errorUtils } from 'utils/errorUtils';

const StyledSpan = styled.span`{
  display: flex;
}`;

const StyledLinkSegment = styled.u`{
  vertical-align: -0.5rem;
}`;

const btnStyle = {
  background: "transparent",
  cursor: "pointer"
};

interface Props extends MessageSourceProps, AuthConsumerRenderProps, NotificationSystemProps {
}

const maxResultsPerPage = 100;

const FileArchiveView = (props: Props) => {

  const {
    selectionNotificationSystem,
    getMessage
  } = props;

  const [allFileArchiveResponse, setAllFileArchiveResponse] =
      useState<FileArchiveDto[]>([]);

  const [fileArchiveResponse, setFileArchiveResponse] =
      useState<FileArchiveDto[]>([]);

  const [fileResponseMap, setFileResponseMap] =
      useState<{ [fileArchiveId: string]: PdfResponse }>({});

  const [totalNumberOfResults, setTotalNumberOfResults] =
      useState(0);

  const [loading, setLoading] = useState<boolean>(true);
  const [activePage, setActivePage] = useState<number>(1);

  const initialSearchFormValuesRef = useRef({fileArchiveSearchTerm: ""});
  const initialSearchFormValues = initialSearchFormValuesRef.current;

  const cancelTokenSourceRef = useRef(axios.CancelToken.source());
  const cancelTokenSource = cancelTokenSourceRef.current;

  useEffect(() => {
    getFileArchives("");
  }, []);

  const handleError = (error: any) => {

    const knownErrors: Array<string> = [
      errorUtils.fileArchiveNotFound,
      errorUtils.fileArchiveContentEmptyDb,
      errorUtils.fileArchiveContentEmptyExternal,
      errorUtils.azureServiceIsDown
    ];

    let errorMsg = getMessage("error.general");
    if (knownErrors.includes(error.message)) {
      errorMsg = getMessage(`error.${error.message}`);
    }

    // @ts-ignore
    const notification = selectionNotificationSystem.current;
    if (notification) {
      notification.addNotification({
        message: <><Icon name="warning sign"/> {getMessage(errorMsg)}</>,
        level: 'error'
      });
    }
  };

  const setSuccessfulMessage = () => {
    // @ts-ignore
    const notification = selectionNotificationSystem.current;
    if (notification) {
      notification.addNotification({
        message: getMessage("fileArchive.successfulMessage"),
        level: 'success'
      });
    }
  }

  const getFileArchives = (search: string) => {

    setLoading(true);
    setActivePage(1);

    findFileArchiveEntries(search, cancelTokenSource)
        .then(response => {
          setAllFileArchiveResponse(response)
          setTotalNumberOfResults(response.length);

          const filteredResponse = response.slice(0, maxResultsPerPage);
          setFileArchiveResponse(filteredResponse)
        })
        .catch((e) => handleError(e.response.data))
        .finally(() => setLoading(false))
  }

  const getDownloadLink = async (fileId: string) => {

    try {
      const response = await getFileDownloadKey(fileId, cancelTokenSource);

      let fileMap = {...fileResponseMap}

      fileMap[fileId] = response;

      setFileResponseMap(fileMap)
    } catch (e) {
      handleError(e.response.data);
    }

  }

  const onPageChange = (e: any, {activePage}: PaginationProps) => {
    setActivePage(activePage as number);

    const pageNumber: number = activePage as number - 1;
    const start = (activePage as number) === 1 ? 0 : (pageNumber * maxResultsPerPage) + pageNumber - 1;
    const end = start + maxResultsPerPage + 1;

    const filteredResponse = allFileArchiveResponse.slice(start, end);
    setFileArchiveResponse(filteredResponse);
  }

  const handleChange = debounce(({values}: any): void => {
    const searchString = values.fileArchiveSearchTerm && values.fileArchiveSearchTerm !== ""
        ? values.fileArchiveSearchTerm
        : "";

    setActivePage(0);
    getFileArchives(searchString)
  }, 200);

  const removeDuplicates = () => {
    setLoading(true);

    removeFileArchiveDuplicates(cancelTokenSource)
        .then(response => {
          setSuccessfulMessage()
        })
        .catch((e) => handleError(e.response.data))
        .finally(() => setLoading(false))
  }

  const renderLoader = (): React.ReactNode => {

    return (
        <Dimmer active inverted>
          <Loader inline="centered" size="medium">{getMessage("fileArchive.loading")}</Loader>
        </Dimmer>
    );
  };

  const renderSearchSegment = (): JSX.Element => {

    return (
        <FinalForm
            onSubmit={() => {
            }}
            initialValues={initialSearchFormValues}
            subscription={{values: true, pristine: true}}
            render={renderSearchForm}
        />
    );
  };

  const renderSearchForm = (): JSX.Element => {

    return (<React.Fragment>
          <Grid.Row>
            <Grid.Column width={3}>
              {getMessage("search")}
            </Grid.Column>
            <Grid.Column width={13}>
              <Field
                  name={"fileArchiveSearchTerm"}
                  component={Input}
                  fluid
                  autoFocus
              />
            </Grid.Column>
            <FormSpy subscription={{values: true}} onChange={handleChange}/>
          </Grid.Row>
        </React.Fragment>
    );
  };

  const renderTableSegment = (): JSX.Element => {
    return (
        <VirtualizedTable
            rowCount={fileArchiveResponse ? fileArchiveResponse.length : undefined}
            rowGetter={fileArchiveRowGetter}
            rowRenderer={fileArchiveRowRenderer}
            rowHeight={42}
            columns={[
              {
                width: 100,
                label: "#",
                dataKey: "index"
              },
              {
                width: 200,
                label: (getMessage("fileArchive.id")),
                dataKey: "id"
              },
              {
                width: 300,
                flexGrow: 1,
                label: (getMessage("fileArchive.source")),
                dataKey: "source"
              },
              {
                width: 300,
                label: (getMessage("fileArchive.keptIn")),
                dataKey: "externalBlobId",
                cellRenderer: (keptInCellRenderer)
              },
              {
                width: 300,
                label: (getMessage("fileArchive.file")),
                dataKey: "content",
                cellRenderer: (downloadContentCellRenderer)
              },
              {
                width: 150,
                label: (getMessage("fileArchive.fileType")),
                dataKey: "fileType"
              }
            ]}
        />
    );
  };

  const fileArchiveRowGetter = ({index}: any) => {
    Object.assign(fileArchiveResponse[index], {index: index + 1});
    return fileArchiveResponse[index]
  };

  const fileArchiveRowRenderer = ({className, columns, index, key, style}: any) => {
    const a11yProps = {'aria-rowindex': index + 1};
    return (
        <div
            {...a11yProps}
            className={className}
            key={key}
            role="row"
            style={style}
        >
          {columns}
        </div>
    );
  };

  const keptInCellRenderer = ({rowData}: any) => {
    const value = rowData.externalBlobId
        ? getMessage("fileArchive.keptIn.external")
        : getMessage("fileArchive.keptIn.db")

    return <div>{value}</div>;
  }

  const downloadContentCellRenderer = ({rowData}: any) => {

    const fileResponse = fileResponseMap[rowData.id] ? fileResponseMap[rowData.id] : undefined;

    return (
        <StyledSpan>
          <div onClick={() => getDownloadLink(rowData.id)}>
            <StyledLinkSegment>
              {getMessage("fileArchive.getDownloadLink")}
            </StyledLinkSegment>
          </div>
          {fileResponse &&
              <Button
                  as="a"
                  download={fileResponse.pdfName}
                  href={getFileUrl(fileResponse.pdfId, fileResponse.pdfName)}
                  style={btnStyle}
              >
                <Icon name={'download'}/>
              </Button>
          }
        </StyledSpan>
    )
  }

  const totalPages = totalNumberOfResults
      ? Math.ceil(totalNumberOfResults / maxResultsPerPage)
      : 1;

  return (
      <PraxStyledSegment>

        <PxGrid>
          {renderSearchSegment()}
          <Grid.Row>
            <Grid.Column width={3}>
              <Button
                  type="button"
                  onClick={() => removeDuplicates()}
              >
                {getMessage("fileArchive.removeDuplicates.button")}
              </Button>
            </Grid.Column>
          </Grid.Row>
        </PxGrid>

        <StyledViewTableRow>
          {loading ? renderLoader() : renderTableSegment()}
        </StyledViewTableRow>

        <Pagination
            activePage={activePage}
            boundaryRange={1}
            onPageChange={onPageChange}
            size="small"
            siblingRange={1}
            totalPages={totalPages}
            disabled={!!(totalNumberOfResults && totalNumberOfResults <= 1)}
        />

      </PraxStyledSegment>
  );
}

export default withAuthContext(withMessages(withNotificationSystemContext(FileArchiveView)));
